import { useState, type ReactNode } from 'react'
import type { ShipDto } from '@/api/hooks'
import { ShipPickerFilterBar } from './ShipPickerFilterBar'
import { ShipPickerRow, blockReason } from './ShipPickerRow'
import { COMBAT_SORTS, shipMatchesFilter, sortShips, useShipFilter, type ShipSortKey } from './useShipFilter'

/**
 * Liste ordonnée de sélection de navires, partagée par les onze écrans qui
 * demandent d'armer une sortie. Le premier coché mène la flotte : l'ordre de
 * `selectedIds` est porteur de sens, et le badge « tête » le rend lisible.
 *
 * Le parent garde l'état de sélection et le cap. Le filtre, lui, vit ici et se
 * perd à la fermeture du panneau : un filtre qui survivrait réapparaîtrait sans
 * que rien à l'écran ne l'explique.
 */
export function ShipPickerList({
  ships,
  selectedIds,
  onToggle,
  cap,
  onSelectMany,
  onClearAll,
  meta,
  sorts = COMBAT_SORTS,
  fleetWord = 'flotte',
  accentClass,
}: {
  /** Toutes les coques à quai, mobilisées et épaves comprises. */
  ships: ShipDto[]
  selectedIds: number[]
  onToggle: (id: number) => void
  cap?: number
  onSelectMany?: (ids: number[]) => void
  onClearAll?: () => void
  meta?: (ship: ShipDto) => ReactNode
  sorts?: ShipSortKey[]
  /** « flotte » ou « convoi » : pilote TOUS les libellés de ce composant. */
  fleetWord?: string
  accentClass?: string
}) {
  const filter = useShipFilter(sorts)
  const [ranksOpen, setRanksOpen] = useState(false)

  // Une coque peut avoir quitté le quai pendant que le panneau était ouvert :
  // ces fantômes ne doivent compter ni dans N ni dans le payload (précédent
  // documenté sur place, ScoutLauncher « Post only the still-docked ids »).
  const alive = selectedIds.filter((id) => ships.some((s) => s.id === id))

  const selectable = ships.filter((s) => !s.mobilized || alive.includes(s.id))
  const matching = selectable.filter((s) => shipMatchesFilter(s, filter.hulls, filter.modules))
  const shown = sortShips(matching, filter.sort)

  // Cochées mais hors filtre : épinglées, dans l'ordre de SÉLECTION (jamais du
  // tri), pour que le navire de tête soit toujours la première ligne.
  const pinned = alive
    .filter((id) => !shown.some((s) => s.id === id))
    .map((id) => ships.find((s) => s.id === id))
    .filter((s): s is ShipDto => Boolean(s))

  const inRanks = ships.filter((s) => s.mobilized && !alive.includes(s.id))
  const effectiveCap = cap ?? ships.length

  const row = (s: ShipDto) => (
    <ShipPickerRow
      key={s.id}
      ship={s}
      checked={alive.includes(s.id)}
      isLead={alive[0] === s.id}
      onToggle={onToggle}
      meta={meta}
      accentClass={accentClass}
    />
  )

  return (
    <div>
      <ShipPickerFilterBar
        ships={ships}
        filter={filter}
        selectedCount={alive.length}
        cap={effectiveCap}
        // On remet les candidats AFFICHÉS, dans l'ordre, sans borner : le cap est
        // appliqué par `selectManyShips` côté parent, seule porte vers l'état de
        // sélection. Le dupliquer ici la rouvrirait.
        onTakeAll={() => onSelectMany?.(shown.filter((s) => !blockReason(s)).map((s) => s.id))}
        onClearAll={() => onClearAll?.()}
      />

      {pinned.length > 0 && (
        <div className="mb-1.5 p-1.5 rounded border border-dashed border-sand-300/30 bg-sand-300/[0.05] space-y-1">
          <p className="text-[10px] uppercase tracking-wider text-sand-500 flex justify-between">
            <span>Dans ta {fleetWord}, hors filtre</span>
            <span className="tabular-nums">{pinned.length}</span>
          </p>
          <div className="max-h-24 overflow-y-auto space-y-1">{pinned.map(row)}</div>
        </div>
      )}

      <div className="space-y-1 max-h-48 overflow-y-auto">
        {shown.length > 0 ? (
          shown.map(row)
        ) : (
          <p className="py-4 text-center text-xs text-ocean-400 italic">
            Aucune coque ne répond à ce filtre. Reprends « Tout » pour revoir ta {fleetWord}.
          </p>
        )}
      </div>

      <div className="flex items-baseline justify-between gap-2 flex-wrap mt-2 pt-1.5 border-t border-sand-500/15 text-[11px] text-ocean-300 tabular-nums">
        <span>
          <strong className={alive.length >= effectiveCap ? 'text-amber-300' : 'text-sand-100'}>
            {alive.length}/{effectiveCap}
          </strong>{' '}
          navires {fleetWord === 'convoi' ? 'chargés' : 'armés'}
        </span>
        <span>{shown.length} affiché{shown.length > 1 ? 's' : ''} sur {ships.length} à quai</span>
      </div>

      {inRanks.length > 0 && (
        <div className="mt-1.5">
          <button
            type="button"
            onClick={() => setRanksOpen((o) => !o)}
            className="w-full flex items-center gap-1.5 px-2 py-1 rounded bg-amber-500/[0.05] border border-amber-400/25 text-[11px] text-amber-300"
          >
            <span>Aux rangs <strong className="tabular-nums">{inRanks.length}</strong></span>
            <span className="flex-1" />
            <span className="text-[10px] opacity-80">bloquées à quai</span>
          </button>
          {ranksOpen && <div className="mt-1 space-y-1 max-h-28 overflow-y-auto">{inRanks.map(row)}</div>}
          <p className="mt-1 text-[10px] text-ocean-400 leading-snug">
            Le branle-bas les garde à la défense de l&apos;île. Elles reprendront la mer une fois les rangs rompus.
          </p>
        </div>
      )}
    </div>
  )
}
